import { useMemo } from 'react';
import { FlaskConical } from 'lucide-react';
import { getActiveDrillItemPath, getActiveNavItemLabel } from '../NavBar/nav-utils';
import type { NavItem } from '../NavBar/nav-types';
import {
  IdentityHeader,
  IdentityTile,
  TakeoverAside,
  takeoverShellClassName,
} from './TakeoverChrome';
import { TakeoverNavGroups, TakeoverSearchField, type TakeoverNavGroup } from './TakeoverNav';
import { useTakeoverNavSearch } from './useTakeoverNavSearch';

export interface LabsShellViewProps {
  /** Organization named in the identity header. */
  orgName: string;
  /** Current pathname, used to highlight the active nav entry. */
  activePath: string;
  /** The Chat entry pinned above the groups; null when not installed. */
  chatItem: NavItem | null;
  /** The Misc and Plugins contents, already permission-filtered. */
  groups: ReadonlyArray<{ label: string; items: NavItem[] }>;
  onBack: () => void;
  /**
   * When true, the Labs aside chrome (back chip, identity, search, nav) is
   * hidden so the immersive page — a game — fills the viewport. Only set
   * by {@link LabsShell} when the active route opts into fullscreen AND the
   * URL flag is on; the game's own header carries the enter/exit toggle
   * (see `@reflex/ui/components/shared/LabsFullscreen`).
   */
  fullscreen: boolean;
  children: React.ReactNode;
}

/**
 * Presentational layout of the Labs takeover: the shared aside chrome with
 * Chat pinned on top and the Misc / Plugins contents as searchable groups,
 * beside the labs pages. The connected {@link LabsShell} wires nav sources,
 * routing, and the escape/back behavior; stories drive this view directly.
 */
export function LabsShellView({
  orgName,
  activePath,
  chatItem,
  groups,
  onBack,
  fullscreen,
  children,
}: LabsShellViewProps) {
  const { query, setQuery, filterGroups } = useTakeoverNavSearch();

  const allGroups = useMemo<TakeoverNavGroup[]>(
    () => [
      ...(chatItem ? [{ label: '', items: [chatItem] }] : []),
      ...groups.map((g) => ({ label: g.label, items: g.items })),
    ],
    [chatItem, groups],
  );
  const visibleGroups = filterGroups(allGroups);

  const navItems = useMemo(() => allGroups.flatMap((g) => g.items), [allGroups]);
  const activeItemPath = useMemo(
    () => getActiveDrillItemPath(navItems, activePath),
    [navItems, activePath],
  );
  const pageTitle = getActiveNavItemLabel(navItems, activePath, 'Labs');

  return (
    <div className={takeoverShellClassName} data-testid="labs-shell">
      {!fullscreen && (
        <TakeoverAside
          back={{ label: 'Back', onClick: onBack, testId: 'labs-back' }}
          header={
            <IdentityHeader
              tile={<IdentityTile icon={FlaskConical} />}
              title="Labs"
              subtitle={orgName}
            />
          }
          pageTitle={pageTitle}
          page={<div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>}
        >
          <TakeoverSearchField query={query} setQuery={setQuery} placeholder="Search labs" />
          <nav
            aria-label="Labs navigation"
            className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-3 scrollbar-fade"
          >
            <TakeoverNavGroups
              groups={visibleGroups}
              activeItemPath={activeItemPath}
              testIdPrefix="labs-nav"
            />
          </nav>
        </TakeoverAside>
      )}
      {fullscreen && <div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>}
    </div>
  );
}
